import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listExtensions } from "../api/extensions";
import { addQueueMember, listQueueMembers, removeQueueMember } from "../api/queues";

/**
 * "Agents who take these transfers" — assigns the operator's own WebRTC agent
 * extensions to a campaign's transfer queue (queue_member rows). Only shown
 * for an existing campaign (there's nothing to attach members to until the
 * campaign has an id — same gating ReplaceCampaignAudio uses).
 *
 * Each checkbox syncs immediately via the queue API rather than waiting for
 * the campaign form's own Save button: membership is a separate resource
 * (queue_member), not a field on the campaign row, so there's nothing for the
 * main save to batch it with.
 */
interface Props {
  campaignId: number;
}

function readApiError(e: unknown): string {
  const ax = e as { response?: { data?: { error?: string; detail?: string } }; message?: string };
  return ax.response?.data?.error || ax.response?.data?.detail || ax.message || "Something went wrong";
}

export function QueueMembersEditor({ campaignId }: Props) {
  const qc = useQueryClient();
  const membersKey = ["queue-members", campaignId];

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: membersKey,
    queryFn: () => listQueueMembers(campaignId),
  });
  const { data: extResp } = useQuery({
    queryKey: ["extensions-web-own"],
    queryFn: () => listExtensions({ per_page: 200, active_only: true }),
  });
  const webExtensions = (extResp?.data ?? []).filter((x) => x.is_web);
  const memberExtensionIds = new Set((members ?? []).map((m) => m.extension_id));

  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: (extensionId: number) => addQueueMember(campaignId, extensionId),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: membersKey });
    },
    onError: (e) => setError(readApiError(e)),
  });
  const remove = useMutation({
    mutationFn: (extensionId: number) => removeQueueMember(campaignId, extensionId),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: membersKey });
    },
    onError: (e) => setError(readApiError(e)),
  });

  function toggle(extensionId: number, checked: boolean) {
    if (checked) add.mutate(extensionId);
    else remove.mutate(extensionId);
  }

  // Which single extension_id has a mutation in flight right now, if any --
  // `mutation.variables` is exactly what `.mutate()` was last called with,
  // so while `isPending` is true it names the row that's actually busy.
  // Previously this was a single page-wide `busy` flag that disabled EVERY
  // checkbox for the duration of any one row's request, making the whole
  // list briefly unusable for an unrelated toggle.
  const pendingExtensionId = add.isPending
    ? add.variables
    : remove.isPending
      ? remove.variables
      : null;

  return (
    <div className="mt-2">
      <label className="block text-xs text-gray-500 mb-1">Agents who take these transfers</label>
      {membersLoading ? (
        <p className="text-xs text-gray-400">Loading agents…</p>
      ) : webExtensions.length === 0 ? (
        <p className="text-xs text-amber-700">
          No WebRTC agent extensions yet — create one under Extensions.
        </p>
      ) : (
        <div className="border border-gray-200 rounded max-h-40 overflow-auto divide-y divide-gray-100">
          {webExtensions.map((x) => (
            <label
              key={x.extension_id}
              className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={memberExtensionIds.has(x.extension_id)}
                disabled={pendingExtensionId === x.extension_id}
                onChange={(e) => toggle(x.extension_id, e.target.checked)}
              />
              <span>{x.title || x.name}</span>
            </label>
          ))}
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-1">
        Agents who can pick up a transferred call for this campaign. Changes take effect
        immediately.
      </p>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}
